import { TaskViewDTO } from 'models/dto/tasks/taskViewDTO';
import { apiGetSharedTaskViews, apiGetTaskViews } from 'services/Api/taskViewService';
import { apiRequest } from 'services/Auth/authConfig';
import { EntityCache, ICache } from './entityCache';

// Lives in globalDataCache so every consumer (TasksOverview, TaskViewMutations,
// NavMain sidebar) reads through one instance. Merges the owned + shared
// endpoints into a single de-duped list keyed by taskViewId.
//
// EntityCache is non-reactive: writes (add/update/remove) mutate items but do
// not notify React. Mutation sites must also update the consumer's local state
// (see TaskViewMutations.ts -- it calls both the API and this cache; see also
// TasksOverview.setCustomViews keeping its mirror of the list in sync).
// Without that double-write, a rename here would leave the sidebar showing the
// old name until a full refresh.
export class TaskViewCache extends EntityCache<TaskViewDTO> implements ICache<TaskViewDTO, TaskViewCache> {
  private isLoading: boolean = false;

  private viewsPromise: Promise<TaskViewDTO[]> | undefined;

  constructor() {
    super();
    this.getId = (view) => view.taskViewId;
    this.getEmptyItem = () => new TaskViewDTO();
  }

  async getItems(refresh: boolean = false): Promise<TaskViewDTO[]> {
    // A re-entrant caller during an in-flight load joins the same promise
    // rather than starting a second owned+shared fetch pair.
    if (this.isLoading && this.viewsPromise) {
      return await this.viewsPromise;
    }
    if (this.items.length > 0 && !refresh) {
      return this.items;
    }
    if (!this.appContext) {
      this.items = [];

      return this.items;
    }

    // isLoading + viewsPromise are assigned synchronously (no await in between)
    // so a concurrent getItems sees the in-flight promise via the guard above.
    this.isLoading = true;
    this.viewsPromise = this.fetchMerged();
    try {
      this.items = await this.viewsPromise;
    } catch (err) {
      this.appContext.setError(err);
    } finally {
      this.isLoading = false;
      this.viewsPromise = undefined;
    }

    return this.items;
  }

  private async fetchMerged(): Promise<TaskViewDTO[]> {
    const accessToken = await this.appContext!.getAccessToken(apiRequest.scopes);
    const [ownedViews, sharedViews] = await Promise.all([
      apiGetTaskViews(accessToken),
      apiGetSharedTaskViews(accessToken),
    ]);

    return [...ownedViews, ...sharedViews].filter(
      (view, index, arr) => arr.findIndex((item) => item.taskViewId === view.taskViewId) === index,
    );
  }

  clone(): TaskViewCache {
    const cache = new TaskViewCache();
    cache.items = this.items?.map((v) => ({ ...v }));

    return cache;
  }
}
